/**
 * Otimização do rateio de créditos como oportunidade de economia (out/2026,
 * pedido do Bruno): a página de Oportunidades mostra o ganho do otimizador
 * (`GET /credits/rateio-analysis`, admin) junto das oportunidades do motor.
 *
 * A otimização é CONJUNTA por distribuidora (todas as UGs de uma vez), então
 * vira UM card por distribuidora, preso à UG de maior excedente entre as que
 * mudam e podem ser contratadas agora — senão a mesma economia seria somada em
 * cada UG. "Contratar" grava uma alteração por UG (`apply`), igual ao
 * "Contratar todas as alterações" da aba Créditos.
 */
import {
  RateioGeneratorView,
  RateioOptimization,
} from 'services/credit.service';
import { ServiceCatalogItem } from 'services/service_requests.service';
import { catalogPriceLabel } from './serviceCatalog';
import { OpportunityOffer, paybackMonths } from './opportunityOffer';

/** % proposto que cada outra UC recebe da UG (só quem recebe algo). */
export const rateioAllocations = (
  analysis: RateioOptimization,
  g: RateioGeneratorView
) =>
  analysis.units
    .filter((u) => u.consumerUnitId !== g.consumerUnitId)
    .map((u) => ({
      consumerUnitId: u.consumerUnitId,
      percentage: u.proposedPct[g.consumerUnitId] ?? 0,
    }))
    .filter((a) => a.percentage > 0);

/** UGs com rateio diferente do atual e fora do intervalo de 3 meses. */
export const rateioApplicableGenerators = (analysis: RateioOptimization) =>
  analysis.generators.filter((g) => g.changed && g.canApply);

/** "UG: Casa 60%, Galpão 40%; retido 0%" — texto do card e do pedido. */
export const rateioSummary = (
  analysis: RateioOptimization,
  g: RateioGeneratorView
) => {
  const nameOf = (id: string) =>
    analysis.units.find((u) => u.consumerUnitId === id)?.name ?? id;
  const parts = rateioAllocations(analysis, g).map(
    (a) => `${nameOf(a.consumerUnitId)} ${a.percentage}%`
  );
  return `${g.name}: ${parts.join(', ') || 'nenhuma UC recebe'}; retido ${
    g.proposedRetainedPct
  }%`;
};

const refMonthLabel = (refMonth: string) => {
  const [year, month] = refMonth.split('_');
  return month && year ? `${month}/${year}` : refMonth;
};

/** Card do rateio para o imóvel, se ele for a UG "âncora" de uma distribuidora. */
export const rateioOfferFor = (
  consumerUnitId: string,
  analyses: RateioOptimization[],
  catalog: ServiceCatalogItem[]
): OpportunityOffer | null => {
  const analysis = analyses.find((a) => {
    const applicable = rateioApplicableGenerators(a);
    if (a.optimized || a.estimatedMonthlySavings <= 0 || !applicable.length) {
      return false;
    }
    const anchor = [...applicable].sort(
      (x, y) => y.monthlySurplusKwh - x.monthlySurplusKwh
    )[0];
    return anchor.consumerUnitId === consumerUnitId;
  });
  if (!analysis) return null;

  const generators = rateioApplicableGenerators(analysis);
  const summaries = generators.map((g) => rateioSummary(analysis, g));
  const item = catalog.find((c) => c.serviceType === 'alteracao_rateio_fv');
  const cost = item?.price ?? null;

  return {
    id: 'rateio',
    title: 'Otimização do rateio de créditos',
    description:
      `Redistribuir os créditos ${
        generators.length > 1 ? 'das UGs' : 'da UG'
      } da ${analysis.utilityCompanyName} para as unidades onde abatem mais ` +
      `na conta (benefício por posto pelo motor do EnergyPass). Novo rateio a ` +
      `partir de ${refMonthLabel(analysis.effectiveFrom)}: ${summaries.join(
        ' · '
      )}.`,
    serviceType: 'alteracao_rateio_fv',
    monthlySavings: analysis.estimatedMonthlySavings,
    cost,
    costLabel: catalogPriceLabel(item) || 'Sob consulta',
    paybackMonths: paybackMonths(cost, analysis.estimatedMonthlySavings),
    rateio: { analysis, generators },
    request: {
      serviceType: 'alteracao_rateio_fv',
      payload: { reason: summaries.join('; ') },
    },
  };
};
